/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Popover } from '@oneaudi/unified-web-components';
import { StyledIconButton } from './AudiHeaderDealerContextStyles';
import React, { lazy, Suspense, useContext } from 'react';
import styled from 'styled-components';
import type { AudiHeaderDealerSearchProps } from '../../interfaces/header-components.interfaces';
import {
  DEALER_CONTEXT_UNSUBSCRIBE,
  KEY_DEALER_DATA,
  NAVIGATION_CLASSNAME,
} from '../../utils/globals';
import DealerSearchIntro from './DealerSearchIntro';
import useToggle from '../../hooks/use-toggle';
import AudiSearchFeatureAppLoader from '../AudiHeaderSearch/AudiSearchFeatureAppLoader';
import { ConfigContext } from '../../context/ConfigContext';
import { useI18n } from '@oneaudi/i18n-context';
import { messages } from '../../i18n/messages';

const DealerSearchFeatureApp = lazy(
  () => import(/* webpackChunkName: "lazy-dealer-search" */ './DealerSearchFeatureApp'),
);

const StyledPopover = styled(Popover)`
  padding: 0 !important;
  margin: 0;
`;

const StyledPopoverContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 392px;
  border-radius: 20px;
  padding: 0;
  overflow-y: scroll;
  scrollbar-width: none;
`;

const StyledButtonContainer = styled.div`
  cursor: pointer;
`;

const DealerSearch: React.FC<AudiHeaderDealerSearchProps> = ({
  dealerSearchGeolocationEnabled = false,
  onClickDealerSearchContext,
  showDealerSearch,
  isMobileView,
}) => {
  const { dealerSearchBenefitEnabled } = useContext(ConfigContext);
  const buttonLabel = useI18n(messages.dealership_select_button);
  const buttonLabelAriaOpen = useI18n(messages.dealership_select_button_aria);
  const buttonLabelAriaClose = useI18n(messages.dealership_close_select_button_aria);
  const [toggleElement, toggle] = useToggle();
  const storageDealerData =
    typeof window !== 'undefined' && sessionStorage?.getItem(KEY_DEALER_DATA);
  const showDealerSearchFA = !dealerSearchBenefitEnabled || showDealerSearch;

  // auto load dealer search for user that is still subscribed
  if (dealerSearchGeolocationEnabled && storageDealerData !== DEALER_CONTEXT_UNSUBSCRIBE) {
    return (
      <Suspense fallback={<AudiSearchFeatureAppLoader fromFA="dealersearch" height />}>
        <DealerSearchFeatureApp
          dealerSearchGeolocationEnabled={dealerSearchGeolocationEnabled}
          showDealerSearch={showDealerSearchFA}
          visible={false}
          isMobileView={isMobileView}
        />
      </Suspense>
    );
  }

  return (
    <StyledPopover
      content={
        <StyledPopoverContainer>
          {showDealerSearchFA ? (
            <Suspense fallback={<AudiSearchFeatureAppLoader fromFA="dealersearch" height />}>
              <DealerSearchFeatureApp
                dealerSearchGeolocationEnabled={false}
                showDealerSearch={showDealerSearchFA}
                visible
                isMobileView={isMobileView}
              />
            </Suspense>
          ) : dealerSearchBenefitEnabled ? (
            <DealerSearchIntro handleSetShowDealerSearch={onClickDealerSearchContext} />
          ) : null}
        </StyledPopoverContainer>
      }
      data-testid="dealerSearch-test-id"
      id={showDealerSearchFA ? 'dealerSearch' : 'dealerSearchIntro'}
      onClose={() => {
        toggle(false);
      }}
      triggerElement={
        <StyledButtonContainer>
          <StyledIconButton
            aria-label={toggleElement ? buttonLabelAriaClose : buttonLabelAriaOpen}
            data-custom-attribute="custom-attribute"
            data-testid="dealer-data-toggle"
            icon={toggleElement ? 'cancel' : 'poi'}
            iconPosition="leading"
            onClick={() => {
              toggle();
              if (!dealerSearchBenefitEnabled) onClickDealerSearchContext();
            }}
            size="md"
            title={buttonLabel}
            variant="tertiary"
            className={NAVIGATION_CLASSNAME}
            // @ts-expect-error
            selected={toggleElement}
          >
            {buttonLabel}
          </StyledIconButton>
        </StyledButtonContainer>
      }
      withTip={false}
      open={toggleElement}
      alignment="center"
      placement="bottom"
    />
  );
};

export default DealerSearch;
